'use client'

import { reportHandledException } from '@/lib/error-reporting'
import { toastTitle } from '@/lib/toast-title'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiError, isTeamMemberDisabledError } from '@kiao-voice/api-client'
import {
  MAX_DICTIONARY_ENTRIES,
  MAX_DICTIONARY_NAME_LENGTH,
  MAX_DICTIONARY_TERM_LENGTH,
} from '@kiao-voice/core'
import { useWorkspaceScopedLoader, type WorkspaceScopedLoadContext } from '@kiao-voice/hooks'
import {
  Button,
  Chip,
  Chips,
  Field,
  IconButton,
  InlineEdit,
  Input,
  KiaoModal,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import { WorkspacePageHeader } from '@/components/workspace-indicator'
import { Plus, Trash2 } from 'lucide-react'
import { useAuthModal } from '@/features/auth/auth-modal-provider'
import { onAuthTokenChange, readStoredToken } from '@/features/auth/token-storage'
import { normalizeHint } from '@kiao-voice/core'
import {
  createDictionary,
  createDictionaryEntry,
  deleteDictionary,
  deleteDictionaryEntry,
  getDictionary,
  listDictionaries,
  updateDictionary,
} from '@/features/dictionary/api-client'
import type { DictionaryDetail, DictionarySummary } from '@/features/dictionary/types'

const MAX_DICTIONARIES = 50

type Translate = ReturnType<typeof useTranslation<'web'>>['t']

interface Props {
  hasTokenFn?: () => boolean
  listDictionariesFn?: typeof listDictionaries
  getDictionaryFn?: typeof getDictionary
  createDictionaryFn?: typeof createDictionary
  updateDictionaryFn?: typeof updateDictionary
  deleteDictionaryFn?: typeof deleteDictionary
  createDictionaryEntryFn?: typeof createDictionaryEntry
  deleteDictionaryEntryFn?: typeof deleteDictionaryEntry
}

interface DictionaryDraft {
  name: string
  description: string
}

interface FormMessage {
  text: string
  tone: 'error' | 'notice'
}

const hasStoredToken = () => readStoredToken() !== null

function statusOf(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined
}

function FormMessageLine({ message }: { message: FormMessage }) {
  return (
    <p
      role={message.tone === 'error' ? 'alert' : 'status'}
      className={`mt-[var(--gap-sm)] text-[length:var(--text-sm)] ${message.tone === 'error' ? 'text-[var(--error)]' : 'text-muted'}`}
    >
      {message.text}
    </p>
  )
}

export function DictionariesPage({
  hasTokenFn = hasStoredToken,
  listDictionariesFn = listDictionaries,
  getDictionaryFn = getDictionary,
  createDictionaryFn = createDictionary,
  updateDictionaryFn = updateDictionary,
  deleteDictionaryFn = deleteDictionary,
  createDictionaryEntryFn = createDictionaryEntry,
  deleteDictionaryEntryFn = deleteDictionaryEntry,
}: Props) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const { open: openSignIn } = useAuthModal()
  const [dictionaries, setDictionaries] = useState<DictionarySummary[]>([])
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error' | 'needsAuth'>('loading')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detail, setDetail] = useState<DictionaryDetail | null>(null)
  const [detailPhase, setDetailPhase] = useState<'loading' | 'ready' | 'error'>('loading')
  const [draft, setDraft] = useState<DictionaryDraft | null>(null)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const loadWorkspace = useCallback(
    async ({ isCurrent }: WorkspaceScopedLoadContext) => {
      if (!hasTokenFn()) {
        if (isCurrent()) setPhase('needsAuth')
        return
      }
      try {
        const next = await listDictionariesFn()
        if (!isCurrent()) return
        setDictionaries(next)
        setSelectedId((current) =>
          next.some((dictionary) => dictionary.id === current) ? current : (next[0]?.id ?? null)
        )
        setPhase('ready')
      } catch (error) {
        reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
        if (!isCurrent()) return
        console.error('[dictionaries] failed to load', error)
        const status = statusOf(error)
        setPhase(
          (status === 401 || status === 403) && !isTeamMemberDisabledError(error)
            ? 'needsAuth'
            : 'error'
        )
      }
    },
    [hasTokenFn, listDictionariesFn]
  )

  const clearWorkspace = useCallback(() => {
    setDictionaries([])
    setPhase('loading')
    setSelectedId(null)
    setDetail(null)
    setDetailPhase('loading')
    setDraft(null)
    setDraftError(null)
    setSaving(false)
  }, [])

  const { reload: load } = useWorkspaceScopedLoader({
    load: loadWorkspace,
    clear: clearWorkspace,
  })

  // The needsAuth CTA opens the sign-in modal without navigation, so the page
  // must reload itself when a token lands (or is cleared in another tab).
  useEffect(() => onAuthTokenChange(() => void load()), [load])

  // Read summaries through a ref so this effect re-runs only when the
  // SELECTION changes — refreshDetail/commitIdentity update `dictionaries`
  // with .map, and keying the effect on the array would wipe the editor,
  // flash loading, and refetch after every single mutation.
  const dictionariesRef = useRef(dictionaries)
  dictionariesRef.current = dictionaries
  useEffect(() => {
    if (selectedId === null) {
      setDetail(null)
      return
    }
    const summary = dictionariesRef.current.find((dictionary) => dictionary.id === selectedId)
    if (!summary) return
    let active = true
    setDetail({ ...summary, entries: [] })
    setDetailPhase('loading')
    void getDictionaryFn(selectedId)
      .then((next) => {
        if (!active) return
        setDetail(next)
        setDetailPhase('ready')
      })
      .catch((error) => {
        reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
        console.error('[dictionaries] failed to load entries', error)
        if (active) setDetailPhase('error')
      })
    return () => {
      active = false
    }
  }, [getDictionaryFn, selectedId])

  const refreshDetail = useCallback(
    async (id: number) => {
      const next = await getDictionaryFn(id)
      setDictionaries((current) =>
        current.map((dictionary) =>
          dictionary.id === id ? { ...dictionary, entryCount: next.entryCount } : dictionary
        )
      )
      setDetail((current) => (current?.id === id ? next : current))
    },
    [getDictionaryFn]
  )

  const commitIdentity = useCallback(
    async (changes: Partial<Pick<DictionarySummary, 'name' | 'description'>>) => {
      if (!detail) return
      const updated = await updateDictionaryFn(detail.id, {
        name: changes.name ?? detail.name,
        description: changes.description ?? detail.description,
      })
      setDictionaries((current) =>
        current.map((dictionary) => (dictionary.id === updated.id ? updated : dictionary))
      )
      setDetail((current) => (current?.id === updated.id ? { ...current, ...updated } : current))
    },
    [detail, updateDictionaryFn]
  )

  const handleCreate = async () => {
    if (!draft) return
    if (!draft.name.trim()) {
      setDraftError(t('dictionaries.needName'))
      return
    }
    setSaving(true)
    setDraftError(null)
    try {
      await createDictionaryFn({ name: draft.name.trim(), description: draft.description.trim() })
      setDraft(null)
      await load()
      toast.success(t('dictionaries.savedToast'))
    } catch (error) {
      reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
      console.error('[dictionaries] create failed', error)
      setDraftError(
        statusOf(error) === 422 ? t('dictionaries.limitReached') : t('dictionaries.updateFailed')
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!detail) return
    const current = detail
    const ok = await confirm({
      title: t('dictionaries.deleteTitle'),
      message: t('dictionaries.deleteConfirm', { count: current.entryCount }),
      confirmText: t('dictionaries.deleteOk'),
      cancelText: t('dictionaries.cancel'),
    })
    if (!ok) return
    try {
      await deleteDictionaryFn(current.id)
      await load()
      toast.success(t('dictionaries.deletedToast', { name: current.name }))
    } catch (error) {
      reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
      console.error('[dictionaries] delete failed', error)
      toast.error(t('dictionaries.deleteFailed'))
    }
  }

  const handleAddEntry = useCallback(
    async (term: string) => {
      if (!detail) return
      await createDictionaryEntryFn(detail.id, { term })
      try {
        await refreshDetail(detail.id)
      } catch (error) {
        reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
        console.error('[dictionaries] failed to refresh entries', error)
        toast.error(t('dictionaries.refreshFailed'))
      }
    },
    [createDictionaryEntryFn, detail, refreshDetail, t]
  )

  const handleDeleteEntry = useCallback(
    async (entryId: number) => {
      if (!detail) return
      try {
        await deleteDictionaryEntryFn(detail.id, entryId)
      } catch (error) {
        reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
        console.error('[dictionaries] entry delete failed', error)
        toast.error(t('dictionaries.entryDeleteFailed'))
        return
      }
      try {
        await refreshDetail(detail.id)
      } catch (error) {
        reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
        console.error('[dictionaries] failed to refresh entries', error)
        toast.error(t('dictionaries.refreshFailed'))
      }
    },
    [deleteDictionaryEntryFn, detail, refreshDetail, t]
  )

  const atDictionaryCap = dictionaries.length >= MAX_DICTIONARIES

  return (
    <>
      <WorkspacePageHeader
        title={t('dictionaries.pageTitle')}
        subtitle={t('dictionaries.pageLead')}
        actions={
          <Button
            variant="primary"
            size="sm"
            disabled={atDictionaryCap}
            onClick={() => {
              setDraftError(null)
              setDraft({ name: '', description: '' })
            }}
          >
            {t('dictionaries.new')}
          </Button>
        }
      />

      {phase === 'loading' ? (
        <p className="py-[var(--gap-md)] text-[length:var(--text-sm)] text-muted">
          {t('dictionaries.loading')}
        </p>
      ) : null}
      {phase === 'error' ? (
        <p className="py-[var(--gap-md)] text-[length:var(--text-sm)] text-muted">
          {t('dictionaries.loadFailed')}
        </p>
      ) : null}
      {phase === 'needsAuth' ? (
        <div className="border-t border-border pt-[var(--gap-md)] text-[length:var(--text-sm)] text-muted">
          <p className="font-medium text-foreground">{t('dictionaries.needAuthHeading')}</p>
          <p className="mt-[var(--gap-xs)]">{t('dictionaries.needAuthBody')}</p>
          <Button
            className="mt-[var(--gap-md)]"
            size="sm"
            onClick={() => openSignIn({ redirectTo: null })}
          >
            {t('dictionaries.signInCta')}
          </Button>
        </div>
      ) : null}

      {phase === 'ready' && dictionaries.length === 0 ? (
        <section className="mt-[var(--gap-md)] max-w-[40em] border-t border-border pt-[var(--gap-md)]">
          <h2 className="text-[length:var(--text-lg)] font-medium">
            {t('dictionaries.emptyHeading')}
          </h2>
          <p className="mt-[var(--gap-xs)] text-[length:var(--text-sm)] text-muted">
            {t('dictionaries.emptyBody')}
          </p>
          <Button
            className="mt-[var(--gap-md)]"
            size="sm"
            onClick={() => {
              setDraftError(null)
              setDraft({ name: '', description: '' })
            }}
          >
            {t('dictionaries.new')}
          </Button>
        </section>
      ) : null}

      {phase === 'ready' && dictionaries.length > 0 ? (
        <div className="mt-[var(--gap-lg)] grid grid-cols-[280px_minmax(0,1fr)] gap-[var(--gap-lg)] max-[860px]:grid-cols-1">
          <aside>
            <div className="flex flex-col" role="list">
              {dictionaries.map((dictionary) => (
                <button
                  key={dictionary.id}
                  type="button"
                  aria-current={dictionary.id === selectedId ? 'true' : undefined}
                  onClick={() => setSelectedId(dictionary.id)}
                  className={`flex w-full flex-col items-start gap-0.5 border-b border-border px-2.5 py-2.5 text-left last:border-b-0 hover:bg-[var(--fg-soft)] ${dictionary.id === selectedId ? 'bg-[var(--fg-soft)] shadow-[inset_2px_0_0_var(--fg)]' : ''}`}
                >
                  <span className="max-w-full truncate text-[length:var(--text-sm)] font-medium">
                    {dictionary.name}
                  </span>
                  <span className="font-mono text-[length:var(--text-xs)] text-muted">
                    {t('dictionaries.entryCount', { count: dictionary.entryCount })}
                  </span>
                </button>
              ))}
            </div>
            {atDictionaryCap ? (
              <p className="px-2.5 pt-3 text-[length:var(--text-xs)] text-muted">
                {t('dictionaries.limitCaption')}
              </p>
            ) : null}
          </aside>

          {detail ? (
            <DictionaryEditor
              detail={detail}
              phase={detailPhase}
              onCommitIdentity={commitIdentity}
              onDelete={() => void handleDelete()}
              onAddEntry={handleAddEntry}
              onDeleteEntry={handleDeleteEntry}
              t={t}
            />
          ) : null}
        </div>
      ) : null}

      {draft ? (
        <CreateDictionaryModal
          draft={draft}
          error={draftError}
          saving={saving}
          onChange={setDraft}
          onClose={() => setDraft(null)}
          onSave={() => void handleCreate()}
          t={t}
        />
      ) : null}
    </>
  )
}

function DictionaryEditor({
  detail,
  phase,
  onCommitIdentity,
  onDelete,
  onAddEntry,
  onDeleteEntry,
  t,
}: {
  detail: DictionaryDetail
  phase: 'loading' | 'ready' | 'error'
  onCommitIdentity: (
    changes: Partial<Pick<DictionarySummary, 'name' | 'description'>>
  ) => Promise<void>
  onDelete: () => void
  onAddEntry: (term: string) => Promise<void>
  onDeleteEntry: (entryId: number) => Promise<void>
  t: Translate
}) {
  return (
    <section data-testid="dictionary-editor" className="min-w-0">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <InlineEdit
            as="h2"
            aria-label={t('dictionaries.nameLabel')}
            value={detail.name}
            maxLength={MAX_DICTIONARY_NAME_LENGTH}
            className="text-[length:var(--fs-dictionary-name)] font-semibold leading-[1.3]"
            onCommit={(name) => onCommitIdentity({ name })}
            onError={() => toast.error(toastTitle(t('dictionaries.updateFailed')))}
          />
          <InlineEdit
            as="p"
            aria-label={t('dictionaries.descriptionLabel')}
            value={detail.description}
            maxLength={200}
            placeholder={t('dictionaries.descriptionPlaceholder')}
            className="mt-1 text-[length:var(--text-sm)] text-muted"
            onCommit={(description) => onCommitIdentity({ description })}
            onError={() => toast.error(toastTitle(t('dictionaries.updateFailed')))}
          />
        </div>
        <IconButton
          variant="ghost"
          aria-label={t('dictionaries.deleteLabel')}
          onClick={onDelete}
          className="ml-auto grid size-[34px] place-items-center border border-border text-muted hover:border-[var(--error)] hover:text-[var(--error)] focus-visible:border-[var(--error)] focus-visible:text-[var(--error)]"
        >
          <Trash2 aria-hidden="true" className="size-4" />
        </IconButton>
      </div>

      <div className="mt-[var(--gap-md)] border-t border-border pt-[var(--gap-md)]">
        {phase === 'loading' ? (
          <p className="text-[length:var(--text-sm)] text-muted">{t('dictionaries.loading')}</p>
        ) : null}
        {phase === 'error' ? (
          <p className="text-[length:var(--text-sm)] text-muted">{t('dictionaries.loadFailed')}</p>
        ) : null}
        {phase === 'ready' ? (
          <Entries detail={detail} onAddEntry={onAddEntry} onDeleteEntry={onDeleteEntry} t={t} />
        ) : null}
      </div>
    </section>
  )
}

function Entries({
  detail,
  onAddEntry,
  onDeleteEntry,
  t,
}: {
  detail: DictionaryDetail
  onAddEntry: (term: string) => Promise<void>
  onDeleteEntry: (entryId: number) => Promise<void>
  t: Translate
}) {
  const [term, setTerm] = useState('')
  const [message, setMessage] = useState<FormMessage | null>(null)
  const [adding, setAdding] = useState(false)
  const atEntryCap = detail.entries.length >= MAX_DICTIONARY_ENTRIES
  const existingTerms = useMemo(
    () => new Set(detail.entries.map((entry) => entry.term)),
    [detail.entries]
  )

  const add = async () => {
    if (adding || atEntryCap) return
    const next = term.trim()
    if (!next) {
      setMessage({ text: t('dictionaries.entryNeedTerm'), tone: 'error' })
      return
    }
    if (existingTerms.has(next)) {
      setMessage({ text: t('dictionaries.entryDuplicate', { term: next }), tone: 'notice' })
      return
    }
    setAdding(true)
    setMessage(null)
    try {
      await onAddEntry(next)
      setTerm('')
    } catch (error) {
      reportHandledException(error, { source: 'app/app/dictionaries/dictionaries-page.tsx' })
      console.error('[dictionaries] add entry failed', error)
      // A backend 422 carries no machine code and can mean either the 300-entry
      // cap or a duplicate that slipped past the local pre-check.
      setMessage({
        text:
          statusOf(error) === 422
            ? t('dictionaries.entryLimitOrDuplicate')
            : t('dictionaries.entryAddFailed'),
        tone: statusOf(error) === 422 ? 'notice' : 'error',
      })
    } finally {
      setAdding(false)
    }
  }

  return (
    <>
      <div className="mb-2.5 flex items-baseline gap-2.5">
        <h3 className="text-[length:var(--text-sm)] font-medium">
          {t('dictionaries.entriesHeading')}
        </h3>
        <span
          className={`ml-auto font-mono text-[length:var(--text-xs)] ${atEntryCap ? 'text-[var(--error)]' : 'text-muted'}`}
        >
          {t('dictionaries.entriesCount', { count: detail.entries.length })}
        </span>
      </div>
      <div className="flex max-w-[420px] items-center gap-2">
        <input
          value={term}
          maxLength={MAX_DICTIONARY_TERM_LENGTH}
          disabled={atEntryCap}
          placeholder={t('dictionaries.entryFieldPlaceholder')}
          onChange={(event) => {
            setTerm(event.target.value)
            setMessage(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void add()
            }
          }}
          className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-background px-3 py-2 text-[length:var(--text-sm)] text-foreground placeholder:text-muted focus-visible:outline-2 focus-visible:outline-[var(--fg-soft)]"
        />
        <IconButton
          variant="ghost"
          aria-label={t('dictionaries.entryAddLabel')}
          disabled={adding || atEntryCap}
          onClick={() => void add()}
        >
          <Plus aria-hidden="true" className="size-4" />
        </IconButton>
      </div>
      <p className="mt-2 text-[length:var(--text-xs)] text-muted">{t('dictionaries.entryHint')}</p>
      {message ? <FormMessageLine message={message} /> : null}
      {detail.entries.length > 0 ? (
        <Chips className="mt-[var(--gap-md)]">
          {detail.entries.map((entry) => {
            const excluded = normalizeHint(entry.term) === null
            return (
              <Chip
                key={entry.id}
                className={excluded ? 'border-dashed text-muted' : undefined}
                onRemove={() => void onDeleteEntry(entry.id)}
                removeLabel={t('dictionaries.entryDeleteLabel', { term: entry.term })}
              >
                {entry.term}
                {excluded ? (
                  <span className="font-mono text-2xs text-muted">
                    {t('dictionaries.entryLongHint')}
                  </span>
                ) : null}
              </Chip>
            )
          })}
        </Chips>
      ) : (
        <p className="mt-[var(--gap-md)] text-[length:var(--text-sm)] text-muted">
          {t('dictionaries.entriesEmpty')}
        </p>
      )}
    </>
  )
}

function CreateDictionaryModal({
  draft,
  error,
  saving,
  onChange,
  onClose,
  onSave,
  t,
}: {
  draft: DictionaryDraft
  error: string | null
  saving: boolean
  onChange: (draft: DictionaryDraft) => void
  onClose: () => void
  onSave: () => void
  t: Translate
}) {
  return (
    <KiaoModal
      open
      onClose={onClose}
      title={t('dictionaries.createTitle')}
      closeLabel={t('dictionaries.close')}
      footer={
        <>
          <span className="spacer" />
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('dictionaries.cancel')}
          </Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={onSave}>
            {t('dictionaries.save')}
          </Button>
        </>
      }
    >
      <Field label={t('dictionaries.fieldName')} htmlFor="dict-name">
        <Input
          id="dict-name"
          value={draft.name}
          placeholder={t('dictionaries.fieldNamePlaceholder')}
          maxLength={MAX_DICTIONARY_NAME_LENGTH}
          onChange={(event) => onChange({ ...draft, name: event.target.value })}
        />
      </Field>
      <Field label={t('dictionaries.fieldDesc')} htmlFor="dict-desc">
        <Input
          id="dict-desc"
          value={draft.description}
          placeholder={t('dictionaries.fieldDescPlaceholder')}
          maxLength={200}
          onChange={(event) => onChange({ ...draft, description: event.target.value })}
        />
      </Field>
      {error ? (
        <p
          role="alert"
          className="mt-[var(--gap-sm)] text-[length:var(--text-sm)] text-[var(--error)]"
        >
          {error}
        </p>
      ) : null}
    </KiaoModal>
  )
}
